import React from 'react';
import { CheckCircle } from '@phosphor-icons/react';
import { AlertCircle, Info, Upload, ChevronDown } from '../../../icons';
import { campaignCopy } from '@discovr/features';
import type { CreatorNote, ImportResult, ImportTarget } from '@discovr/types';
import { fmtDeliverableType } from '@discovr/types';
import { Button } from '../../../components/ui/button';

interface ImportResultsPanelProps {
  /** Required for a pipeline import (the default); unused for outreach, which
   *  reads outreachResult instead — the two result shapes don't overlap
   *  (outreach has no sheet-column/creator-note breakdown). */
  result?: ImportResult;
  /** Default 'pipeline'. */
  mode?: ImportTarget;
  /** Outreach mode only — the committed batch's final counts plus the
   *  roster entry ids of every item that reached created/matched (ImportGet's
   *  `items[]`, see outreachWire.ts's toOutreachImportBatch) — what "Send
   *  first touch to all" actually sends. */
  outreachResult?: OutreachResult;
  /** Outreach mode only — "Send first touch to all" (ruling 78). Hidden
   *  (not just disabled) when not supplied. */
  onSendFirstTouch?: (entryIds: string[]) => void;
  /** Outreach mode only (P1-33); shown only when something failed. */
  outreachRetry?: OutreachRetry;
  /** Dismiss the panel (also fired by the 3s auto-dismiss on a clean import,
   *  unless suppressAutoDismiss is set). */
  onReset: () => void;
  /** "Import new sheet" button — opens the upload modal. Falls back to onReset. */
  onUploadAnother?: () => void;
  /** Disables the 3s auto-dismiss on a clean result — set this when the
   *  caller has something the admin needs to actually read attached to this
   *  result (e.g. a roster-drift-deflection notice rendered above this
   *  panel), so it isn't a race against a countdown the admin didn't know
   *  was running. Default false — every other consumer keeps the original
   *  auto-dismiss behavior. */
  suppressAutoDismiss?: boolean;
}

/** Outreach mode's own results summary (Task 27) — "Added N · Already on this campaign N ·
 *  Skipped N" per ruling 78, in place of the roster breakdown above (column
 *  issues, per-creator rejection reasons) that has no outreach equivalent.
 *  "Send first touch to all" is hidden entirely when onSendFirstTouch isn't
 *  supplied, and disabled (not hidden) when there's simply nothing to send
 *  to yet — createdEntryIds empty is a real, if unlikely, outcome (e.g.
 *  every row matched an existing entry that's already past outreach_sourced). */
interface OutreachResult {
  created: number;
  matched: number;
  skipped: number;
  /** Undefined from an older backend: unknown, so no Failed segment. */
  failed?: number;
  createdEntryIds: string[];
}

/** A failed retry's message is shown by the wizard's own error banner. */
interface OutreachRetry {
  onRetry: () => void;
  retrying: boolean;
}

const failedCopy = campaignCopy.creator.importReview.outreachFailed;

function failedCountOf(result: OutreachResult): number {
  return result.failed ?? 0;
}

function outreachResultsTitle(created: number, failed: number): string {
  if (failed > 0) return failedCopy.heading(failed);
  return `${created} creator${created === 1 ? '' : 's'} added to outreach`;
}

function OutreachResultsIcon({ failed }: { failed: number }) {
  if (failed > 0) return <AlertCircle size={20} className="mt-0.5 shrink-0 text-foreground-warning-default" aria-hidden />;
  return <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" />;
}

function OutreachResultsSummary({
  outreachResult,
  onSendFirstTouch,
  outreachRetry,
  onReset,
}: {
  outreachResult: OutreachResult;
  onSendFirstTouch?: (entryIds: string[]) => void;
  outreachRetry?: OutreachRetry;
  onReset: () => void;
}) {
  const { created, matched, skipped, createdEntryIds } = outreachResult;
  const failed = failedCountOf(outreachResult);
  const canRetry = failed > 0 && outreachRetry !== undefined;
  // Leaving or sending mid-retry would race the retry's result.
  const retrying = outreachRetry?.retrying ?? false;
  const canSendFirstTouch = createdEntryIds.length > 0 && !retrying;
  const counts = [`Added ${created}`, `Already on this campaign ${matched}`, `Skipped ${skipped}`];
  if (outreachResult.failed !== undefined) counts.push(`Failed ${failed}`);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-start gap-3 px-5 py-4">
        <OutreachResultsIcon failed={failed} />
        <div>
          <div className="text-body-sm-semibold text-foreground">{outreachResultsTitle(created, failed)}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">{counts.join(' · ')}</div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
        {canRetry && (
          <Button variant="outline" size="sm" type="button" disabled={outreachRetry.retrying} onClick={outreachRetry.onRetry}>
            {outreachRetry.retrying ? failedCopy.retrying : failedCopy.retry}
          </Button>
        )}
        {onSendFirstTouch && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={!canSendFirstTouch}
            onClick={() => onSendFirstTouch(createdEntryIds)}
          >
            Send first touch to all
          </Button>
        )}
        <Button variant="default" size="sm" type="button" disabled={retrying} onClick={onReset}>
          Done
        </Button>
      </div>
    </div>
  );
}

interface RejectedGroup {
  reason: string;
  notes: CreatorNote[];
}

function creatorLabel(note: CreatorNote): string {
  if (note.username) return `@${note.username}`;
  return note.email ?? `Row ${note.row}`;
}

function formatMissingPrices(types: string[]): string {
  const labels = types.map(fmtDeliverableType);
  if (labels.length === 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0] ?? ''} & ${labels[1] ?? ''}`;
  return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1] ?? ''}`;
}

function gapNoteDetail(note: CreatorNote): React.ReactNode {
  if (note.info) return note.info;
  if ((note.removed?.length ?? 0) > 0) {
    return (
      <>
        <strong className="text-foreground">{formatMissingPrices(note.removed ?? [])}</strong>{' '}
        {(note.removed?.length ?? 0) > 1 ? 'deliverables' : 'deliverable'} removed
      </>
    );
  }
  if ((note.missing_prices?.length ?? 0) > 0) {
    return (
      <>
        <strong className="text-foreground">{formatMissingPrices(note.missing_prices ?? [])}</strong>{' '}
        {(note.missing_prices?.length ?? 0) > 1 ? 'prices' : 'price'} missing — deliverable
        {(note.missing_prices?.length ?? 0) > 1 ? 's' : ''} not added
      </>
    );
  }
  return null;
}

/** Groups rejected notes by their reason, most-common reason first — turns a
 *  flat list of (say) 40 rejections into a scannable "5 missing username · 3
 *  duplicate · ..." breakdown instead of 40 near-identical rows. */
function groupRejected(notes: CreatorNote[]): RejectedGroup[] {
  const order: string[] = [];
  const byReason = new Map<string, CreatorNote[]>();
  for (const note of notes) {
    const reason = note.reject_reason || 'Unknown reason';
    if (!byReason.has(reason)) {
      byReason.set(reason, []);
      order.push(reason);
    }
    byReason.get(reason)?.push(note);
  }
  return order
    .map((reason) => ({ reason, notes: byReason.get(reason) ?? [] }))
    .sort((a, b) => b.notes.length - a.notes.length);
}

const EMPTY_RESULT: ImportResult = {
  imported_at: '',
  imported: 0,
  rejected: 0,
  ignored_columns: [],
  disabled_deliverables: [],
  creator_notes: [],
};

export function ImportResultsPanel({
  result: rawResult,
  mode = 'pipeline',
  outreachResult,
  onSendFirstTouch,
  outreachRetry,
  onReset,
  onUploadAnother,
  suppressAutoDismiss = false,
}: ImportResultsPanelProps) {
  // Roster's derived state below reads off a stable fallback when there's no
  // real result (outreach mode) — every hook still runs unconditionally
  // either way; only the JSX returned at the bottom branches on mode.
  const result = rawResult ?? EMPTY_RESULT;
  const unsupportedDeliverables = result.unsupported_deliverables ?? [];
  const hasColumnIssues =
    result.ignored_columns.length > 0 ||
    result.disabled_deliverables.length > 0 ||
    unsupportedDeliverables.length > 0 ||
    !!result.email_column_ignored;
  const hasCreatorNotes = result.creator_notes.length > 0;
  const isClean = !hasColumnIssues && !hasCreatorNotes;

  const rejectedNotes = React.useMemo(
    () => result.creator_notes.filter((n) => n.status === 'rejected'),
    [result.creator_notes],
  );
  const gapNotes = React.useMemo(
    () => result.creator_notes.filter((n) => n.status === 'imported'),
    [result.creator_notes],
  );
  const rejectedGroups = React.useMemo(() => groupRejected(rejectedNotes), [rejectedNotes]);
  const withGaps = gapNotes.length;

  // Which rejection-reason groups are expanded to their per-creator row list.
  const [expandedReasons, setExpandedReasons] = React.useState<Record<string, boolean>>({});
  // "Imported with a note" is secondary/benign info on an otherwise-successful
  // import — collapsed behind one summary line by default so a clean-ish
  // import still reads as a synopsis, not a wall of per-row cards.
  const [gapsExpanded, setGapsExpanded] = React.useState(false);

  // Auto-dismiss after 3 s on a perfectly clean import. `onReset` is read
  // through a ref so the timer is armed ONCE per clean result — callers pass
  // inline arrows, and putting `onReset` in the deps would clear + restart
  // the 3 s countdown on every parent re-render (query refetches alone can
  // re-render more often than that, so the dismiss would never fire).
  const onResetRef = React.useRef(onReset);
  React.useEffect(() => {
    onResetRef.current = onReset;
  });
  React.useEffect(() => {
    if (mode === 'outreach' || !isClean || suppressAutoDismiss) return;
    const t = setTimeout(() => onResetRef.current(), 3_000);
    return () => clearTimeout(t);
  }, [mode, isClean, suppressAutoDismiss]);

  if (mode === 'outreach') {
    return (
      <OutreachResultsSummary
        outreachResult={outreachResult ?? { created: 0, matched: 0, skipped: 0, createdEntryIds: [] }}
        onSendFirstTouch={onSendFirstTouch}
        outreachRetry={outreachRetry}
        onReset={onReset}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {/* ── Header ── */}
      <div className="border-b border-border px-5 py-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-body-sm-semibold text-foreground">
            {result.imported} creator{result.imported === 1 ? '' : 's'} imported
          </div>
          {result.imported_at && (
            <div className="shrink-0 text-[11px] text-muted-foreground">
              {new Date(result.imported_at).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </div>
          )}
        </div>
        {(result.rejected > 0 || withGaps > 0) && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {[
              result.rejected > 0 && `${result.rejected} rejected`,
              withGaps > 0 && `${withGaps} imported with gaps`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
        {isClean && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">All rows clean ✓</div>
        )}
      </div>

      {/* ── Column-level issues ── */}
      {hasColumnIssues && (
        <div className="border-b border-border bg-muted/60 px-5 py-3">
          <div className="mb-2.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            Column-level issues — affected all creators
          </div>
          <div className="space-y-3">
            {result.ignored_columns.map((col) => (
              <div key={col} className="flex items-start gap-2 text-[12px] text-muted-foreground">
                <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-secondary" />
                <span>
                  <strong className="text-foreground">{col}</strong>
                  {' — '}not a recognised column name, skipped
                </span>
              </div>
            ))}
            {result.disabled_deliverables.map((type) => (
              <div key={type} className="space-y-2">
                <div className="flex items-start gap-2 text-[12px] text-muted-foreground">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-secondary" />
                  <span>
                    <strong className="text-foreground">{fmtDeliverableType(type)}</strong>
                    {' — '}deliverable not enabled for this campaign, skipped for all creators
                  </span>
                </div>
                <div className="ml-3.5 flex items-start gap-2 rounded-lg bg-muted px-3 py-2">
                  <Info size={11} className="mt-0.5 shrink-0 text-muted-foreground" />
                  <span className="text-[11px] leading-relaxed text-muted-foreground">
                    You can still add {fmtDeliverableType(type)} for individual creators later —
                    open a creator&apos;s roster entry and add it from the deliverables section.
                  </span>
                </div>
              </div>
            ))}
            {unsupportedDeliverables.map((type) => (
              <div key={type} className="flex items-start gap-2 text-[12px] text-muted-foreground">
                <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-secondary" />
                <span>
                  <strong className="text-foreground">{fmtDeliverableType(type)}</strong>
                  {' — '}not supported for this campaign, skipped for all creators
                </span>
              </div>
            ))}
            {result.email_column_ignored && (
              <div className="flex items-start gap-2 text-[12px] text-muted-foreground">
                <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-secondary" />
                <span>
                  <strong className="text-foreground">email</strong>
                  {' — '}no longer required, column ignored
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Rejected creators, grouped by reason ── */}
      {rejectedGroups.length > 0 && (
        <div className="border-b border-border px-5 py-3">
          <div className="mb-2.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            Rejected ({rejectedNotes.length})
          </div>
          <div className="space-y-1.5">
            {rejectedGroups.map((group) => {
              const expanded = !!expandedReasons[group.reason];
              return (
                <div key={group.reason} className="overflow-hidden rounded-lg border border-destructive/20 bg-destructive/5">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedReasons((s) => ({ ...s, [group.reason]: !s[group.reason] }))
                    }
                    aria-expanded={expanded}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] text-foreground"
                  >
                    <span>
                      <strong>{group.notes.length}</strong> {group.reason}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {expanded && (
                    <div className="max-h-40 space-y-1 overflow-y-auto border-t border-destructive/20 px-3 py-2 pr-1">
                      {group.notes.map((note, i) => (
                        <div key={i} className="text-[11px] text-muted-foreground">
                          {creatorLabel(note)}{' '}
                          <span className="text-[10px] text-muted-foreground/70">(row {note.row})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Imported-with-a-note, collapsed synopsis by default ── */}
      {gapNotes.length > 0 && (
        <div className="border-b border-border px-5 py-3">
          <button
            type="button"
            onClick={() => setGapsExpanded((v) => !v)}
            aria-expanded={gapsExpanded}
            className="flex w-full items-center justify-between gap-2 text-left"
          >
            <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
              {gapNotes.length} imported with a note{gapNotes.length === 1 ? '' : 's'}
            </span>
            <ChevronDown
              size={14}
              className={`shrink-0 text-muted-foreground transition-transform ${gapsExpanded ? 'rotate-180' : ''}`}
            />
          </button>
          {gapsExpanded && (
            <div className="mt-2.5 max-h-52 space-y-1.5 overflow-y-auto pr-1">
              {gapNotes.map((note, i) => (
                <div key={i} className="rounded-lg border border-border bg-muted/60 px-3 py-2 text-[12px]">
                  <div className="mb-0.5 font-semibold text-foreground">{creatorLabel(note)}</div>
                  <div className="text-[11px] text-muted-foreground">{gapNoteDetail(note)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Footer ── */}
      <div className="flex items-center justify-between px-5 py-3">
        <span className="text-[11px] text-muted-foreground">
          Import a new sheet to add more creators
        </span>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={onUploadAnother ?? onReset}
          className="h-7 gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <Upload size={11} /> Import new sheet
        </Button>
      </div>
    </div>
  );
}
